/**
 * Analytics Service
 * Tracks visitor information and sends it to the `track-visitor` Edge Function.
 * (Previously POSTed to /api/analytics/track on the Render backend.)
 */

import { functionUrl, functionHeaders } from './supabaseFunctions';

// Generate or retrieve session ID
const getSessionId = (): string => {
  let sessionId = sessionStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = `web_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('analytics_session_id', sessionId);
  }
  return sessionId;
};

// Detect device type
const getDeviceType = (): 'desktop' | 'mobile' | 'tablet' => {
  const ua = navigator.userAgent;
  const width = window.innerWidth;
  
  // Check for mobile devices first (more reliable than screen width)
  if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua)) {
    // Distinguish between tablet and mobile
    if (/iPad|Android(?!.*Mobile)/i.test(ua) || (width >= 768 && width < 1024)) {
      return 'tablet';
    }
    return 'mobile';
  }
  
  // Desktop detection
  if (width >= 1024) {
    return 'desktop';
  }
  
  // Fallback based on width
  if (width < 768) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'desktop';
};

// Get device information
const getDeviceInfo = () => {
  const ua = navigator.userAgent;
  
  // Browser detection
  let browser = 'unknown';
  let browserVersion = 'unknown';
  
  if (ua.includes('Chrome') && !ua.includes('Edg')) {
    browser = 'Chrome';
    const match = ua.match(/Chrome\/(\d+)/);
    browserVersion = match ? match[1] : 'unknown';
  } else if (ua.includes('Firefox')) {
    browser = 'Firefox';
    const match = ua.match(/Firefox\/(\d+)/);
    browserVersion = match ? match[1] : 'unknown';
  } else if (ua.includes('Safari') && !ua.includes('Chrome')) {
    browser = 'Safari';
    const match = ua.match(/Version\/(\d+)/);
    browserVersion = match ? match[1] : 'unknown';
  } else if (ua.includes('Edg')) {
    browser = 'Edge';
    const match = ua.match(/Edg\/(\d+)/);
    browserVersion = match ? match[1] : 'unknown';
  }

  // OS detection
  let os = 'unknown';
  let osVersion = 'unknown';
  
  if (ua.includes('Windows')) {
    os = 'Windows';
    const match = ua.match(/Windows NT (\d+\.\d+)/);
    osVersion = match ? match[1] : 'unknown';
  } else if (ua.includes('Mac OS X') || ua.includes('Macintosh')) {
    os = 'macOS';
    const match = ua.match(/Mac OS X (\d+[._]\d+)/);
    osVersion = match ? match[1].replace('_', '.') : 'unknown';
  } else if (ua.includes('Linux')) {
    os = 'Linux';
  } else if (ua.includes('Android')) {
    os = 'Android';
    const match = ua.match(/Android (\d+\.\d+)/);
    osVersion = match ? match[1] : 'unknown';
  } else if (ua.includes('iPhone') || ua.includes('iPad')) {
    os = 'iOS';
    const match = ua.match(/OS (\d+[._]\d+)/);
    osVersion = match ? match[1].replace('_', '.') : 'unknown';
  }

  // Device brand/model (for mobile)
  let brand = null;
  let model = null;
  
  if (ua.includes('iPhone')) {
    brand = 'Apple';
    const match = ua.match(/iPhone(\d+,\d+)?/);
    model = match ? match[0] : 'iPhone';
  } else if (ua.includes('iPad')) {
    brand = 'Apple';
    model = 'iPad';
  } else if (ua.includes('Android')) {
    const match = ua.match(/([A-Z][a-z]+)\s+([A-Z][a-z0-9\s]+)/);
    if (match) {
      brand = match[1];
      model = match[2];
    }
  }

  return {
    type: getDeviceType(),
    brand,
    model,
    os,
    osVersion,
    browser,
    browserVersion
  };
};

// Get screen information
const getScreenInfo = () => {
  return {
    width: window.screen.width,
    height: window.screen.height,
    colorDepth: window.screen.colorDepth
  };
};

// Track page visit
export const trackPageVisit = async (currentPage: string) => {
  try {
    const sessionId = getSessionId();
    const referrer = document.referrer || null;
    
    const trackingData = {
      sessionId,
      currentPage,
      referrer,
      metadata: {
        device: getDeviceInfo(),
        screen: getScreenInfo(),
        language: navigator.language,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      }
    };

    // Send tracking data (fire and forget). The payload shape is unchanged --
    // track-visitor reads the same { sessionId, currentPage, referrer, metadata }.
    fetch(functionUrl('track-visitor'), {
      method: 'POST',
      headers: functionHeaders(),
      body: JSON.stringify(trackingData),
    }).catch((error) => {
      // Silently fail - tracking should not break the app
      console.debug('Analytics tracking failed:', error);
    });
  } catch (error) {
    // Silently fail - tracking should not break the app
    console.debug('Analytics tracking error:', error);
  }
};

// Track page view on route change
export const initializeAnalytics = () => {
  // Track initial page load
  trackPageVisit(window.location.pathname);

  // Track page changes (for SPA routing)
  let lastPath = window.location.pathname;
  
  const trackRouteChange = () => {
    const currentPath = window.location.pathname;
    if (currentPath !== lastPath) {
      trackPageVisit(currentPath);
      lastPath = currentPath;
    }
  };

  // Use MutationObserver to detect route changes
  const observer = new MutationObserver(trackRouteChange);
  observer.observe(document.body, {
    childList: true,
    subtree: true
  });

  // Also listen to popstate events (browser back/forward)
  window.addEventListener('popstate', () => {
    trackPageVisit(window.location.pathname);
  });
};

const analyticsService = {
  trackPageVisit,
  initializeAnalytics
};

export default analyticsService;

